import { AppUrls } from "../../ts/app";
import { Controller } from "../../ts/controller";
import { Logger } from "../../ts/utils/logger";

enum WizardStep {
    accountDetails = 0,
    verify = 1,
    success = 2,
}

export class CreateAccountPageController extends Controller {
    recaptchaSiteKey: string | null = null;
    recaptchaToken: string | null = null;
    passwordsVisible: boolean = false;
    wizardStep: WizardStep = WizardStep.accountDetails;
    loadingSignup: boolean = false;
    loadingVerification: boolean = false;
    errors: string[] = [];
    username: string | null = null;
    email: string | null = null;

    constructor() {
        super({
            logLabel: 'CreateAccountPageController',
        });
    }

    public initialize({
        recaptchaSiteKey,
    }: {
        recaptchaSiteKey: string,
    }): void {
        this.recaptchaSiteKey = recaptchaSiteKey;

        this.bindClick({
            elementId: 'create-account-submit-button',
            handler: this._submitSignup.bind(this),
        });
        this.bindClick({
            elementId: 'verify-button',
            handler: this._submitVerification.bind(this),
        });
        this.bindClick({
            elementId: 'toggle-password-visibility',
            handler: () => {
                this.passwordsVisible = !this.passwordsVisible;
                this._setState();
            },
        });

        this._setState();

        Logger.info(this.logLabel, 'Initialized');
    }

    private _autoFillForm(): void {
        const username = this.getElementByIdOrWarn<HTMLInputElement>(
            'create-account-username');
        const email = this.getElementByIdOrWarn<HTMLInputElement>(
            'create-account-email');
        const password = this.getElementByIdOrWarn<HTMLInputElement>(
            'create-account-password');
        const confirmPassword = this.getElementByIdOrWarn<HTMLInputElement>(
            'create-account-password-confirm');
        if (!username || !email || !password || !confirmPassword) {
            return;
        }
        username.value = 'test1';
        email.value = 'test1@gmail.com';
        password.value = 'Test1234$';
        confirmPassword.value = 'Test1234$$';
    }

    private _setState(): void {
        const createAccountSection = this.getElementByIdOrWarn('create-account-section');
        const verificationSection = this.getElementByIdOrWarn('verification-section');
        const successMessageSection = this.getElementByIdOrWarn('success-message-section');
        const submitButton = this.getElementByIdOrWarn<HTMLButtonElement>('create-account-submit-button');
        const verifyButton = this.getElementByIdOrWarn<HTMLButtonElement>('verify-button');
        const togglePasswordButton = this.getElementByIdOrWarn('toggle-password-visibility');

        if (createAccountSection) createAccountSection.style.display = 'none';
        if (verificationSection) verificationSection.style.display = 'none';
        if (successMessageSection) successMessageSection.style.display = 'none';

        switch (this.wizardStep) {
            case WizardStep.accountDetails:
                if (createAccountSection) createAccountSection.style.display = 'flex';
                this._setButtonLoadingState(submitButton, this.loadingSignup);
                this._setErrorsState('create-account-error-message');

                if (togglePasswordButton) {
                    togglePasswordButton.textContent = this.passwordsVisible ? 'Hide' : 'Show';
                }
                this._setPasswordVisibilityState();
                break;

            case WizardStep.verify:
                if (verificationSection) verificationSection.style.display = 'flex';
                this._setButtonLoadingState(verifyButton, this.loadingVerification);
                this._setErrorsState('create-account-verify-error-message');
                break;

            case WizardStep.success:
                if (successMessageSection) successMessageSection.style.display = 'flex';
                break;

            default:
                Logger.warn(this.logLabel, `Unknown wizard step: ${this.wizardStep}`);
                break;
        }
    }

    private _setButtonLoadingState(
        button: HTMLButtonElement | null,
        loading: boolean,
    ): void {
        if (!button) {
            return;
        }
        const buttonContent = button.querySelector('.button-content') as HTMLElement;
        const buttonSpinner = button.querySelector('.button-spinner') as HTMLElement;
        if (buttonContent && buttonSpinner) {
            buttonContent.style.display = loading ? 'none' : 'block';
            buttonSpinner.style.display = loading ? 'block' : 'none';
        }
        button.disabled = loading;
        button.style.opacity = loading ? '0.6' : '1';
        button.style.cursor = loading ? 'not-allowed' : 'pointer';
    }

    private _setErrorsState(errorElementId: string): void {
        const errorElement = this.getElementByIdOrWarn(errorElementId);
        if (!errorElement) {
            return;
        }
        if (this.errors.length > 0) {
            errorElement.innerHTML = this.errors.join('<br>');
            errorElement.style.display = 'block';
        } else {
            errorElement.style.display = 'none';
            errorElement.innerHTML = '';
        }
    }

    private _setPasswordVisibilityState(): void {
        const passwordField = this.getElementByIdOrWarn<HTMLInputElement>('create-account-password');
        const confirmPasswordField = this.getElementByIdOrWarn<HTMLInputElement>('create-account-password-confirm');
        if (!passwordField || !confirmPasswordField) {
            return;
        }
        const type = this.passwordsVisible ? 'text' : 'password';
        passwordField.type = type;
        confirmPasswordField.type = type;
    }

    private async _submitSignup(): Promise<void> {
        this.errors = [];
        this.loadingSignup = true;
        this._setState();

        const usernameField = this.getElementByIdOrWarn<HTMLInputElement>('create-account-username');
        const emailField = this.getElementByIdOrWarn<HTMLInputElement>('create-account-email');
        const passwordField = this.getElementByIdOrWarn<HTMLInputElement>('create-account-password');
        const confirmPasswordField = this.getElementByIdOrWarn<HTMLInputElement>('create-account-password-confirm');
        if (!usernameField || !emailField || !passwordField || !confirmPasswordField) {
            this.errors.push("Cannot submit signup");
            return;
        }

        const username = usernameField.value.trim();
        const email = emailField.value.trim().toLowerCase();
        const password = passwordField.value.trim();
        const confirmPassword = confirmPasswordField.value.trim();

        const formValid = this._validateSignupForm(username, email, password, confirmPassword);
        if (!formValid) {
            this.loadingSignup = false;
            this._setState();
            return;
        }

        try {
            if (!this.recaptchaSiteKey) {
                throw Error('Recaptcha not configured correctly');
            }
            this.recaptchaToken = await grecaptcha.execute(
                this.recaptchaSiteKey,
                { action: 'create_account' },
            );
        } catch (error) {
            Logger.error(this.logLabel, `reCAPTCHA failed ${error}`);
            this.errors = ["Something went wrong. Please try again."];
            this.loadingSignup = false;
            this._setState();
            return;
        }

        const createAccountData = {
            username,
            email,
            password,
            recaptcha_token: this.recaptchaToken,
        };

        try {
            const response = await fetch(AppUrls.createAccountEndpoint, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(createAccountData),
            });

            if (response.ok) {
                this.wizardStep = WizardStep.verify;
                this.errors = [];
                this.username = username;
                this.email = email;
            } else {
                const data = await response.json();
                this.errors = this.parseErrors(data);
            }
        } catch (error) {
            Logger.error(this.logLabel, `Create account failed ${error}`);
            this.errors = ["An error occurred. Please try again."];
        } finally {
            this.loadingSignup = false;
            this._setState();
        }
    }

    private async _submitVerification(): Promise<void> {
        this.errors = [];

        const codeField = this.getElementByIdOrWarn<HTMLInputElement>('verification-code');
        if (!codeField) {
            this.errors.push("Cannot submit verification");
            return;
        }

        const code = codeField?.value.trim();
        if (!code) {
            this.errors = ["Verification code is required."];
            this._setState();
            return;
        }

        this.loadingVerification = true;
        this._setState();
        const verificationData = {
            username: this.username,
            email: this.email,
            code,
        };

        try {
            const response = await fetch(AppUrls.verifyEmailEndpoint, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(verificationData),
            });

            if (response.ok) {
                this.wizardStep = WizardStep.success;
                this.errors = [];
            } else {
                const data = await response.json();
                this.errors = this.parseErrors(data);
            }
        } catch (error) {
            Logger.error(this.logLabel, `Verification failed\n${error}`);
            this.errors = ["An error occurred. Please try again."];
        } finally {
            this.loadingVerification = false;
            this._setState();
        }
    }

    private _validateSignupForm(
        username: string,
        email: string,
        password: string,
        confirmPassword: string,
    ): boolean {
        this.errors = [];
        if (!username) this.errors.push("Username is required.");
        if (!email || !/^[\w.%+-]+@[A-Za-z0-9.-]+\.[A-Z]{2,}$/i.test(email)) {
            this.errors.push("Invalid email address.");
        }
        if (!password || !confirmPassword) this.errors.push("Password and confirmation are required.");
        if (password !== confirmPassword) this.errors.push("Passwords do not match.");
        return this.errors.length === 0;
    }
}
